import React, { useState, useEffect, useCallback } from 'react';
import { FaSync, FaDownload, FaArrowUp, FaArrowDown, FaDollarSign, FaUsers, FaUtensils, FaShoppingCart } from 'react-icons/fa';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import analyticsApiService from '../services/analyticsApi';

const Analytics = () => {
  // Состояния для данных
  const [overallStats, setOverallStats] = useState({});
  const [dailySalesData, setDailySalesData] = useState([]);
  const [popularDishes, setPopularDishes] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [orderTimeStats, setOrderTimeStats] = useState([]);
  const [paymentMethodStats, setPaymentMethodStats] = useState([]);
  const [orderTypeStats, setOrderTypeStats] = useState([]);
  const [userActivityStats, setUserActivityStats] = useState([]);
  
  // Состояния для UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEstablishment, setSelectedEstablishment] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [establishments, setEstablishments] = useState([]);

  // Функция для форматирования чисел
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('ru-RU').format(Math.round(num));
  };

  // Функция для форматирования валюты
  const formatCurrency = (num) => {
    if (num === null || num === undefined) return '₽0';
    return `₽${formatNumber(num)}`;
  };

  // Кастомный Tooltip для графиков
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name === 'Выручка' || entry.name === 'revenue' ? 
                formatCurrency(entry.value) : formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Загрузка заведений
  const loadEstablishments = useCallback(async () => {
    try {
      const data = await analyticsApiService.getEstablishments();
      setEstablishments(data);
    } catch (error) {
      console.error('Ошибка загрузки заведений:', error);
      // Не показываем ошибку пользователю, так как это не критично
    }
  }, []);

  // Загрузка всех данных аналитики
  const loadAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const params = {
      establishment: selectedEstablishment !== 'all' ? selectedEstablishment : undefined,
      period: selectedPeriod
    };

    try {
      // Загружаем данные параллельно, но обрабатываем ошибки отдельно
      const promises = [
        analyticsApiService.getOverallStats(params).catch(err => ({ error: err, data: {} })),
        analyticsApiService.getDailySalesData(params).catch(err => ({ error: err, data: [] })),
        analyticsApiService.getPopularDishes(params).catch(err => ({ error: err, data: [] })),
        analyticsApiService.getMonthlyRevenueData(params).catch(err => ({ error: err, data: [] })),
        analyticsApiService.getOrderTimeStats(params).catch(err => ({ error: err, data: [] })),
        analyticsApiService.getPaymentMethodStats(params).catch(err => ({ error: err, data: [] })),
        analyticsApiService.getOrderTypeStats(params).catch(err => ({ error: err, data: [] })),
        analyticsApiService.getUsersAnalytics(params).catch(err => ({ error: err, data: [] }))
      ];

      const results = await Promise.all(promises);
      
      // Обрабатываем результаты
      setOverallStats(results[0].error ? {} : results[0]);
      setDailySalesData(results[1].error ? [] : results[1]);
      setPopularDishes(results[2].error ? [] : results[2]);
      setRevenueData(results[3].error ? [] : results[3]);
      setOrderTimeStats(results[4].error ? [] : results[4]);
      setPaymentMethodStats(results[5].error ? [] : results[5]);
      setOrderTypeStats(results[6].error ? [] : results[6]);
      setUserActivityStats(results[7].error ? [] : results[7]);

      // Проверяем, есть ли критические ошибки
      const criticalErrors = results.filter(r => r.error && r.error.response?.status !== 404);
      if (criticalErrors.length > 0) {
        console.warn('Некоторые данные аналитики недоступны:', criticalErrors);
      }

    } catch (error) {
      console.error('Ошибка загрузки аналитики:', error);
      setError('Ошибка загрузки данных аналитики');
    } finally {
      setLoading(false);
    }
  }, [selectedEstablishment, selectedPeriod]);

  // Загрузка данных при монтировании и изменении фильтров
  useEffect(() => {
    loadEstablishments();
  }, [loadEstablishments]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // Обработка экспорта отчета
  const handleExport = async () => {
    try {
      const params = {
        establishment: selectedEstablishment !== 'all' ? selectedEstablishment : undefined,
        period: selectedPeriod
      };
      
      const blob = await analyticsApiService.exportReport(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Ошибка экспорта:', error);
      alert('Ошибка при экспорте отчета');
    }
  };

     // Обработка популярных блюд
   const processedPopularDishesData = Array.isArray(popularDishes) ? popularDishes.map((dish, index) => ({
     ...dish,
     rank: index + 1,
     percentage: popularDishes.length > 0 ? ((dish.orders_count / popularDishes[0].orders_count) * 100).toFixed(1) : 0
   })) : [];

  // Цвета для графиков
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Заголовок и фильтры */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Аналитика</h1>
          <p className="text-gray-600">Статистика и отчеты по работе ресторана</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Селектор заведения */}
          <select
            value={selectedEstablishment}
            onChange={(e) => setSelectedEstablishment(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Все заведения</option>
            {establishments.map(est => (
              <option key={est.id} value={est.id}>{est.name}</option>
            ))}
          </select>

          {/* Селектор периода */}
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="today">Сегодня</option>
            <option value="week">Неделя</option>
            <option value="month">Месяц</option>
            <option value="quarter">Квартал</option>
            <option value="year">Год</option>
          </select>

          {/* Кнопки действий */}
          <div className="flex gap-2">
            <button
              onClick={loadAnalyticsData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <FaSync className="w-4 h-4" />
              Обновить
            </button>
            
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <FaDownload className="w-4 h-4" />
              Экспорт
            </button>
          </div>
        </div>
      </div>

      {/* Сообщение об ошибке */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Ошибка загрузки данных</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI карточки */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-lg border border-blue-200 p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center">
            <div className="p-3 bg-blue-500 rounded-xl shadow-md">
              <FaShoppingCart className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-blue-700">Заказы</p>
              <p className="text-2xl font-bold text-blue-900">{formatNumber(overallStats.total_orders)}</p>
              <p className="text-xs text-blue-600">
                {overallStats.orders_change > 0 ? '+' : ''}{overallStats.orders_change || 0}% к пред. периоду
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-lg border border-green-200 p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center">
            <div className="p-3 bg-green-500 rounded-xl shadow-md">
              <FaDollarSign className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-green-700">Выручка</p>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(overallStats.total_revenue)}</p>
              <p className="text-xs text-green-600">
                {overallStats.revenue_change > 0 ? '+' : ''}{overallStats.revenue_change || 0}% к пред. периоду
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-lg border border-purple-200 p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center">
            <div className="p-3 bg-purple-500 rounded-xl shadow-md">
              <FaDollarSign className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-purple-700">Средний чек</p>
              <p className="text-2xl font-bold text-purple-900">{formatCurrency(overallStats.average_order_value)}</p>
              <p className="text-xs text-purple-600">
                {overallStats.avg_change > 0 ? '+' : ''}{overallStats.avg_change || 0}% к пред. периоду
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-lg border border-orange-200 p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center">
            <div className="p-3 bg-orange-500 rounded-xl shadow-md">
              <FaUsers className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-orange-700">Клиенты</p>
              <p className="text-2xl font-bold text-orange-900">{formatNumber(overallStats.total_customers)}</p>
              <p className="text-xs text-orange-600">
                {overallStats.customers_change > 0 ? '+' : ''}{overallStats.customers_change || 0}% к пред. периоду
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-lg border border-red-200 p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center">
            <div className="p-3 bg-red-500 rounded-xl shadow-md">
              <FaUtensils className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-red-700">Активные блюда</p>
              <p className="text-2xl font-bold text-red-900">{formatNumber(overallStats.active_dishes)}</p>
              <p className="text-xs text-red-600">В меню</p>
            </div>
          </div>
        </div>
      </div>

      {/* Основные графики */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                 {/* График продаж */}
         <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
           <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
             <FaShoppingCart className="w-5 h-5 text-blue-500 mr-2" />
             Динамика продаж
           </h3>
           {Array.isArray(dailySalesData) && dailySalesData.length > 0 ? (
             <ResponsiveContainer width="100%" height={300}>
               <ComposedChart data={dailySalesData}>
                 <CartesianGrid strokeDasharray="3 3" />
                 <XAxis dataKey="date" />
                 <YAxis yAxisId="left" />
                 <YAxis yAxisId="right" orientation="right" />
                 <Tooltip content={<CustomTooltip />} />
                 <Legend />
                 <Bar yAxisId="left" dataKey="orders" fill="#3B82F6" name="Заказы" />
                 <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={3} name="Выручка" />
               </ComposedChart>
             </ResponsiveContainer>
           ) : (
             <div className="text-center text-gray-500 py-8">
               <p>Нет данных о продажах</p>
             </div>
           )}
         </div>

        {/* График статусов заказов */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FaShoppingCart className="w-5 h-5 text-purple-500 mr-2" />
            Статусы заказов
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={overallStats.order_statuses || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {(overallStats.order_statuses || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

             {/* Популярные блюда */}
       <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
         <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
           <FaUtensils className="w-5 h-5 text-orange-500 mr-2" />
           Популярные блюда
         </h3>
         <div className="space-y-3">
           {processedPopularDishesData.length > 0 ? (
             processedPopularDishesData.slice(0, 5).map((dish) => (
               <div key={dish.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                 <div className="flex items-center space-x-4">
                   <div className="flex-shrink-0">
                     <span className={`inline-flex items-center justify-center h-10 w-10 rounded-full text-white text-sm font-bold shadow-md ${
                       dish.rank === 1 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                       dish.rank === 2 ? 'bg-gradient-to-br from-gray-400 to-gray-600' :
                       dish.rank === 3 ? 'bg-gradient-to-br from-orange-400 to-orange-600' :
                       'bg-gradient-to-br from-blue-400 to-blue-600'
                     }`}>
                       {dish.rank}
                     </span>
                   </div>
                   <div>
                     <h4 className="text-sm font-semibold text-gray-900">{dish.name}</h4>
                     <p className="text-sm text-gray-600">{formatNumber(dish.orders_count)} заказов</p>
                   </div>
                 </div>
                 <div className="text-right">
                   <p className="text-sm font-bold text-green-700">{formatCurrency(dish.total_revenue)}</p>
                   <p className="text-xs text-gray-500">{dish.percentage}% от топа</p>
                 </div>
               </div>
             ))
           ) : (
             <div className="text-center text-gray-500 py-8">
               <FaUtensils className="w-12 h-12 text-gray-300 mx-auto mb-3" />
               <p>Нет данных о популярных блюдах</p>
             </div>
           )}
         </div>
       </div>

      {/* Дополнительная аналитика */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                 {/* Выручка по месяцам */}
         <div className="bg-white rounded-lg shadow p-6">
           <h3 className="text-lg font-medium text-gray-900 mb-4">Выручка по месяцам</h3>
           {Array.isArray(revenueData) && revenueData.length > 0 ? (
             <ResponsiveContainer width="100%" height={300}>
               <ComposedChart data={revenueData}>
                 <CartesianGrid strokeDasharray="3 3" />
                 <XAxis dataKey="month" />
                 <YAxis />
                 <Tooltip content={<CustomTooltip />} />
                 <Legend />
                 <Bar dataKey="revenue" fill="#10B981" name="Выручка" />
                 <Line type="monotone" dataKey="orders" stroke="#3B82F6" strokeWidth={3} name="Заказы" />
               </ComposedChart>
             </ResponsiveContainer>
           ) : (
             <div className="text-center text-gray-500 py-8">
               <p>Нет данных о выручке</p>
             </div>
           )}
         </div>

                 {/* Статистика времени заказов */}
         <div className="bg-white rounded-lg shadow p-6">
           <h3 className="text-lg font-medium text-gray-900 mb-4">Время заказов</h3>
           <div className="space-y-4">
             {Array.isArray(orderTimeStats) && orderTimeStats.length > 0 ? (
               orderTimeStats.map((timeSlot, index) => (
                 <div key={index} className="flex items-center justify-between">
                   <div className="flex items-center space-x-3">
                     <div className={`w-3 h-3 rounded-full ${
                       index === 0 ? 'bg-blue-500' : 
                       index === 1 ? 'bg-green-500' : 
                       index === 2 ? 'bg-yellow-500' : 'bg-red-500'
                     }`}></div>
                     <span className="text-sm font-medium text-gray-900">{timeSlot.time_range}</span>
                   </div>
                   <div className="text-right">
                     <span className="text-sm font-medium text-gray-900">{formatNumber(timeSlot.orders_count)}</span>
                     <span className="text-xs text-gray-500 ml-2">({timeSlot.percentage}%)</span>
                   </div>
                 </div>
               ))
             ) : (
               <div className="text-center text-gray-500 py-4">
                 <p>Нет данных о времени заказов</p>
               </div>
             )}
           </div>
         </div>
      </div>

      {/* Способы оплаты и типы заказов */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                 {/* Способы оплаты */}
         <div className="bg-white rounded-lg shadow p-6">
           <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
             </svg>
             Способы оплаты
           </h3>
           <div className="space-y-4">
             {Array.isArray(paymentMethodStats) && paymentMethodStats.length > 0 ? (
               paymentMethodStats.map((method, index) => (
                 <div key={index} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                   <div className="flex items-center justify-between mb-3">
                     <div className="flex items-center space-x-3">
                       <div 
                         className="w-4 h-4 rounded-full"
                         style={{ backgroundColor: method.color || '#6B7280' }}
                       ></div>
                       <span className="text-sm font-medium text-gray-900">{method.method}</span>
                     </div>
                     <div className="text-right">
                       <span className="text-sm font-bold text-gray-900">{formatNumber(method.count)} заказов</span>
                       <span className="text-xs text-gray-500 ml-2">({method.percentage}%)</span>
                     </div>
                   </div>
                   
                   {/* Сумма в рублях */}
                   <div className="flex items-center justify-between mb-2">
                     <span className="text-xs text-gray-600">Сумма:</span>
                     <div className="text-right">
                       <span className="text-sm font-semibold text-green-600">{formatCurrency(method.total_amount || 0)}</span>
                       <span className="text-xs text-gray-500 ml-2">({method.amount_percentage || 0}%)</span>
                     </div>
                   </div>
                   
                   <div className="w-full bg-gray-200 rounded-full h-2">
                     <div 
                       className="h-2 rounded-full transition-all duration-300" 
                       style={{ 
                         width: `${method.percentage}%`,
                         backgroundColor: method.color || '#6B7280'
                       }}
                     ></div>
                   </div>
                 </div>
               ))
             ) : (
               <div className="text-center text-gray-500 py-8">
                 <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3 3v8a3 3 0 003 3z" />
                 </svg>
                 <p>Нет данных о способах оплаты</p>
               </div>
             )}
           </div>
         </div>

                 {/* Типы заказов */}
         <div className="bg-white rounded-lg shadow p-6">
           <h3 className="text-lg font-medium text-gray-900 mb-4">Типы заказов</h3>
           {Array.isArray(orderTypeStats) && orderTypeStats.length > 0 ? (
             <ResponsiveContainer width="100%" height={200}>
               <PieChart>
                 <Pie
                   data={orderTypeStats}
                   cx="50%"
                   cy="50%"
                   innerRadius={40}
                   outerRadius={80}
                   paddingAngle={5}
                   dataKey="count"
                 >
                   {orderTypeStats.map((entry, index) => (
                     <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                   ))}
                 </Pie>
                 <Tooltip />
                 <Legend />
               </PieChart>
             </ResponsiveContainer>
           ) : (
             <div className="text-center text-gray-500 py-8">
               <p>Нет данных о типах заказов</p>
             </div>
           )}
         </div>
      </div>

             {/* Активность пользователей */}
       <div className="bg-white rounded-lg shadow p-6">
         <h3 className="text-lg font-medium text-gray-900 mb-4">Активность пользователей</h3>
         {Array.isArray(userActivityStats) && userActivityStats.length > 0 ? (
           <ResponsiveContainer width="100%" height={300}>
             <ComposedChart data={userActivityStats}>
               <CartesianGrid strokeDasharray="3 3" />
               <XAxis dataKey="date" />
               <YAxis />
               <Tooltip />
               <Legend />
               <Bar dataKey="active_users" fill="#8884d8" name="Активные пользователи" />
               <Line type="monotone" dataKey="new_users" stroke="#82ca9d" name="Новые пользователи" />
             </ComposedChart>
           </ResponsiveContainer>
         ) : (
           <div className="text-center text-gray-500 py-8">
             <p>Нет данных об активности пользователей</p>
           </div>
         )}
       </div>

      {/* Статус системы */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Статус системы</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Авторизация</span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              ✓ Активна
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Навигация</span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              ✓ Работает
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">API подключение</span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              ✓ Исправлено
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 